import * as React from "react";

export interface SyncPlanItem {
  resource: string;
  sourceId: number;
  targetId: number | null;
  name: string;
  action: string;
  note: string;
}

export interface SiteSyncResult {
  success: boolean;
  isDryRun: boolean;
  conflictMode: string;
  plan: SyncPlanItem[];
  errors: string[];
}

interface Props {
  result: SiteSyncResult;
}

const ACTION_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  create:    { bg: "bg-green-100",  text: "text-green-800",  label: "Create" },
  overwrite: { bg: "bg-blue-100",   text: "text-blue-800",   label: "Overwrite" },
  rename:    { bg: "bg-amber-100",  text: "text-amber-800",  label: "Rename" },
  skip:      { bg: "bg-gray-100",   text: "text-gray-500",   label: "Skip" }
};

const RESOURCE_LABELS: Record<string, string> = {
  workflow:     "Workflow",
  aiconfig:     "AI Bot",
  botprofile:   "Bot Profile",
  sitesettings: "Site Settings",
  deployment:   "Deployment"
};

export default function SyncPlanTable({ result }: Props) {
  const { plan, errors, isDryRun } = result;

  const counts = React.useMemo(() => {
    const c = { create: 0, overwrite: 0, rename: 0, skip: 0 };
    for (const item of plan) {
      const key = item.action as keyof typeof c;
      if (key in c) c[key]++;
    }
    return c;
  }, [plan]);

  return (
    <div className="mt-4">
      {/* Header / dry-run badge */}
      {isDryRun && (
        <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          <span className="font-semibold">Preview only</span> — no changes have been applied.
        </div>
      )}

      {/* Summary bar */}
      {plan.length === 0 ? (
        <div className="p-4 text-center text-gray-500 bg-gray-50 rounded border border-gray-200">
          No changes needed — source and target are already in sync.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-3 mb-4">
            {counts.create > 0 && (
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                {counts.create} to create
              </span>
            )}
            {counts.overwrite > 0 && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {counts.overwrite} to overwrite
              </span>
            )}
            {counts.rename > 0 && (
              <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                {counts.rename} to rename
              </span>
            )}
            {counts.skip > 0 && (
              <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                {counts.skip} to skip
              </span>
            )}
          </div>

          <div className="overflow-x-auto rounded border border-gray-200">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2 font-medium text-gray-600">Type</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Name</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Action</th>
                  <th className="px-4 py-2 font-medium text-gray-600">Note</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((item, idx) => {
                  const colors = ACTION_COLORS[item.action] ?? ACTION_COLORS.skip;
                  return (
                    <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-500 text-xs uppercase tracking-wide">
                        {RESOURCE_LABELS[item.resource] ?? item.resource}
                      </td>
                      <td className="px-4 py-2 font-medium text-gray-800">{item.name}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${colors.bg} ${colors.text}`}>
                          {colors.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-500 text-xs">{item.note}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Errors */}
      {errors && errors.length > 0 && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded">
          <div className="font-semibold text-red-700 mb-2">Errors ({errors.length})</div>
          <ul className="list-disc list-inside space-y-1">
            {errors.map((err, i) => (
              <li key={i} className="text-red-600 text-sm">{err}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
